import { useEffect, useMemo, useState } from 'react';
import { Modal } from 'antd';
import { AiOutlineClose } from 'react-icons/ai';
import { CaretDownOutlined, CaretRightOutlined } from '@ant-design/icons';
import { useForm, FormProvider } from 'react-hook-form';
import clsx from 'clsx';
import InputField from '../../../../shared/components/InputField';
import CustomWarning from '../../../../shared/components/CustomWarning';
import { InputFieldType } from '../../../../enums/inputFieldTypes';
import {
  useCreateGroup,
  useEditGroup,
  useGroupDetails,
  useGroupFileTree,
  useGroupIcons,
  useGroupMapOptions,
  useGroupUserOptions,
} from '../../../../hooks/useGroupManager';
import type { GroupReference } from '../../../../services/GroupManager/groupManager.service';
import ChecklistPanel from './ChecklistPanel';
import FilePermissionsPanel from './FilePermissionsPanel';
import GroupIconPicker from './GroupIconPicker';
import { GROUP_FORM_SECTIONS, GroupFormSection } from './constants';
import styles from './groupFormModal.module.scss';

export interface GroupFormModalProps {
  open: boolean;
  onClose: () => void;
  /** The group to edit; omitted when creating a new group. */
  groupId?: number;
}

interface GroupFormValues {
  groupName: string;
}

interface SectionButtonProps {
  sectionKey: GroupFormSection;
  label: string;
  isActive: boolean;
  hasSelection: boolean;
  onSelect: (section: GroupFormSection) => void;
}

// ChecklistPanel is shared with the permissions picker, which works with string ids.
const toChecklistItems = (options: GroupReference[]) => options.map((option) => ({ id: String(option.id), name: option.name }));

// Map titles are stored as rich-text HTML (e.g. "<p>Title</p>"). DOMParser gives
// the plain text without running scripts or loading images from the markup.
const getTextFromHtml = (html: string) => new DOMParser().parseFromString(html, 'text/html').body.textContent?.trim() ?? '';

// Everything the form edits besides the name (react-hook-form tracks that one).
interface GroupSelection {
  iconId: number | null;
  userIds: string[];
  mapIds: string[];
  folderIds: number[];
  fileIds: number[];
}

const EMPTY_SELECTION: GroupSelection = { iconId: null, userIds: [], mapIds: [], folderIds: [], fileIds: [] };

const sameIds = (left: Array<string | number>, right: Array<string | number>) => {
  const rightSet = new Set(right);
  return left.length === right.length && left.every((id) => rightSet.has(id));
};

const isSameSelection = (left: GroupSelection, right: GroupSelection) =>
  left.iconId === right.iconId &&
  sameIds(left.userIds, right.userIds) &&
  sameIds(left.mapIds, right.mapIds) &&
  sameIds(left.folderIds, right.folderIds) &&
  sameIds(left.fileIds, right.fileIds);

const COLLAPSED_WIDTH = 563;
const EXPANDED_WIDTH = 888;

const SectionButton = ({ sectionKey, label, isActive, hasSelection, onSelect }: SectionButtonProps) => {
  const handleClick = () => onSelect(sectionKey);

  return (
    <button
      type="button"
      className={clsx(styles.sectionButton, isActive ? styles.sectionButtonActive : hasSelection && styles.sectionButtonHasSelection)}
      aria-expanded={isActive}
      onClick={handleClick}
    >
      <span>{label}</span>
      {isActive ? <CaretDownOutlined /> : <CaretRightOutlined />}
    </button>
  );
};

const GroupFormModal = ({ open, onClose, groupId }: GroupFormModalProps) => {
  const methods = useForm<GroupFormValues>({ defaultValues: { groupName: '' } });
  const { dirtyFields } = methods.formState;

  const [activeSection, setActiveSection] = useState<GroupFormSection | null>(null);
  const [selection, setSelection] = useState<GroupSelection>(EMPTY_SELECTION);
  // What the form started from: empty when creating, the saved group when editing.
  const [baseline, setBaseline] = useState<GroupSelection>(EMPTY_SELECTION);
  const [isPrefilled, setIsPrefilled] = useState(false);
  const [isExitWarningOpen, setIsExitWarningOpen] = useState(false);

  const isEditing = groupId !== undefined;
  const { userOptions } = useGroupUserOptions(open);
  const { groupIcons, isLoadingGroupIcons } = useGroupIcons(open);
  const { mapOptions } = useGroupMapOptions(open);
  const { fileTree } = useGroupFileTree(open);
  const { groupDetails, isFetchingGroupDetails } = useGroupDetails(open ? groupId : undefined);
  const createGroup = useCreateGroup();
  const editGroup = useEditGroup();
  const userItems = useMemo(() => toChecklistItems(userOptions), [userOptions]);
  const mapItems = useMemo(() => toChecklistItems(mapOptions.map((map) => ({ ...map, name: getTextFromHtml(map.name) }))), [mapOptions]);
  const sectionHasSelection: Record<GroupFormSection, boolean> = {
    users: selection.userIds.length > 0,
    mapPermissions: selection.mapIds.length > 0,
    filePermissions: selection.folderIds.length > 0 || selection.fileIds.length > 0,
  };

  // Edit saves replace the group's users, maps and permissions wholesale, so the
  // form must hold the full saved group before SAVE is allowed.
  const isPrefilling = isEditing && !isPrefilled;
  useEffect(() => {
    if (!open || !isEditing || !isPrefilling || !groupDetails || isFetchingGroupDetails || isLoadingGroupIcons) return;
    const saved: GroupSelection = {
      // A group saved with an icon outside the catalog starts unselected and must pick a new one.
      iconId: groupIcons.find((icon) => icon.url === groupDetails.iconUrl)?.id ?? null,
      userIds: groupDetails.userIds.map(String),
      mapIds: groupDetails.mapIds.map(String),
      folderIds: groupDetails.folderIds,
      fileIds: groupDetails.fileIds,
    };
    methods.reset({ groupName: groupDetails.name });
    setSelection(saved);
    setBaseline(saved);
    setIsPrefilled(true);
  }, [open, isEditing, isPrefilling, groupDetails, isFetchingGroupDetails, isLoadingGroupIcons, groupIcons, methods]);

  const title = isEditing ? 'EDIT GROUP' : 'CREATE NEW GROUP';
  const confirmLabel = isEditing ? 'SAVE' : 'CREATE';
  const isSaving = createGroup.isPending || editGroup.isPending;
  const activeSectionConfig = GROUP_FORM_SECTIONS.find((section) => section.key === activeSection);
  const hasUnsavedChanges = Object.keys(dirtyFields).length > 0 || !isSameSelection(selection, baseline);

  const updateSelection = (changes: Partial<GroupSelection>) => setSelection((current) => ({ ...current, ...changes }));
  const handleUsersChange = (userIds: string[]) => updateSelection({ userIds });
  const handleMapsChange = (mapIds: string[]) => updateSelection({ mapIds });

  const handleToggleSection = (section: GroupFormSection) => {
    setActiveSection((current) => (current === section ? null : section));
  };

  const handleCollapseActiveSection = () => {
    if (activeSection) handleToggleSection(activeSection);
  };

  const handleCloseRequest = () => {
    if (hasUnsavedChanges) {
      setIsExitWarningOpen(true);
      return;
    }
    onClose();
  };

  // The picker lives inside the name input, so its error uses that field's error
  // slot. With a name entered, the only error that slot can hold is the icon one.
  const handleSelectIcon = (id: number) => {
    updateSelection({ iconId: id });
    if (methods.getValues('groupName').trim()) methods.clearErrors('groupName');
  };

  const handleSubmit = methods.handleSubmit(async ({ groupName }) => {
    const icon = groupIcons.find((option) => option.id === selection.iconId);
    if (!icon) {
      methods.setError('groupName', { type: 'icon', message: 'Select a group icon' });
      return;
    }
    const payload = {
      name: groupName.trim(),
      iconUrl: icon.url,
      userIds: selection.userIds.map(Number),
      mapIds: selection.mapIds.map(Number),
      folderIds: selection.folderIds,
      fileIds: selection.fileIds,
    };
    try {
      if (isEditing) await editGroup.execute(groupId, payload);
      else await createGroup.execute(payload);
      onClose();
    } catch {
      // The hook already shows the error; keep the form open so it can be corrected.
    }
  });

  const handleExitCancel = () => setIsExitWarningOpen(false);

  const handleExitConfirm = () => {
    setIsExitWarningOpen(false);
    onClose();
  };

  return (
    <>
      <Modal
        open={open}
        onCancel={handleCloseRequest}
        width={activeSection ? EXPANDED_WIDTH : COLLAPSED_WIDTH}
        centered
        footer={null}
        closeIcon={null}
        rootClassName={styles.groupFormModalRoot}
        className={styles.groupFormModal}
        destroyOnClose
      >
        <button type="button" className={styles.closeButton} onClick={handleCloseRequest} aria-label="Close">
          <AiOutlineClose />
        </button>
        <h2 className={styles.title}>{title}</h2>
        <div className={styles.content}>
          <FormProvider {...methods}>
            <div className={styles.primaryColumn}>
              <InputField
                name="groupName"
                type={InputFieldType.TEXT}
                label="GROUP NAME"
                size="large"
                className={styles.nameField}
                rules={{ validate: (value: string) => value.trim().length > 0 || 'Group name is required' }}
                suffix={<GroupIconPicker icons={groupIcons} selectedIconId={selection.iconId} onSelect={handleSelectIcon} />}
              />
              <div className={styles.sections}>
                {GROUP_FORM_SECTIONS.map(({ key, label }) => (
                  <SectionButton
                    key={key}
                    sectionKey={key}
                    label={label}
                    isActive={activeSection === key}
                    hasSelection={sectionHasSelection[key]}
                    onSelect={handleToggleSection}
                  />
                ))}
              </div>
            </div>
          </FormProvider>
          <div className={clsx(styles.sidePanel, activeSection && styles.sidePanelOpen)}>
            {activeSection ? (
              <>
                <button type="button" className={styles.sidePanelHeader} onClick={handleCollapseActiveSection}>
                  <span>{activeSectionConfig?.label}</span>
                  <CaretDownOutlined />
                </button>
                {activeSection === 'users' ? (
                  <ChecklistPanel items={userItems} selectedIds={selection.userIds} onChange={handleUsersChange} searchPlaceholder="SEARCH USERS" emptyMessage="No users found" />
                ) : null}
                {activeSection === 'mapPermissions' ? (
                  <ChecklistPanel items={mapItems} selectedIds={selection.mapIds} onChange={handleMapsChange} searchPlaceholder="SEARCH MAPS" emptyMessage="No maps found" />
                ) : null}
                {activeSection === 'filePermissions' ? (
                  <FilePermissionsPanel
                    tree={fileTree}
                    selectedFolderIds={selection.folderIds}
                    selectedFileIds={selection.fileIds}
                    onChange={updateSelection}
                  />
                ) : null}
              </>
            ) : null}
          </div>
        </div>
        <div className={styles.footer}>
          <button type="button" className={styles.confirmButton} onClick={handleSubmit} disabled={isSaving || isPrefilling}>
            {confirmLabel}
            <span className={styles.caret} aria-hidden />
          </button>
        </div>
      </Modal>
      <CustomWarning
        open={isExitWarningOpen}
        onCancel={handleExitCancel}
        onConfirm={handleExitConfirm}
        message="Exit group editor? Your progress will not be saved!"
        cancelText="Back"
        confirmText="Discard"
        variant="dark"
        maskClosable
        maskVisible
      />
    </>
  );
};

export default GroupFormModal;
